import { useState, useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'

import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { FormAmountInput } from 'Components/Inputs/FormAmountInput'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ControlledSwitch } from 'Components/Inputs/Switch/ControlledSwitch'
import { ControlledSelect } from 'Components/Inputs/ControlledSelect'
import { UpdateBenefitProgramInfo, EndBenefitProgramPolicyInfo } from './ModifyBenefitProgramInfo'
import { BenefitProgramStartDateSelector } from './BenefitProgramStartDateSelector'
import { BenefitProgramEndDateSelector } from './BenefitProgramEndDateSelector'
import { useBenefitProgramForm } from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/useBenefitProgramForm'

import { ButtonContainer, FlexRow, spacingLG, spacingMD } from 'Utils/styles/spacing'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { RolloverOptions } from 'Utils/types/benefitProgramPolicy'
import { normalizeTypedAmount } from 'Utils/helpers/currency'

type BenefitProgramPolicyFormProps = {
  selectedProgram: EmployerBenefitProgram
  closeModal: () => void
}

export enum EditMode {
  Update = 'update',
  End = 'end'
}

export const BenefitProgramPolicyForm = ({
  selectedProgram,
  closeModal
}: BenefitProgramPolicyFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    currencySymbol,
    errors,
    rollover,
    setRollover,
    rolloverOptions,
    handleUpdatePolicy,
    handleEndPolicy,
    isAnnual,
    isOneTime,
    handleError,
    benefitProgramRegisterOptions
  } = useBenefitProgramForm(closeModal, selectedProgram)

  const ongoingPurchasesEnabled = useWatch({ control, name: 'ongoingPurchases' })

  const [editMode, setEditMode] = useState<null | EditMode>(null)

  const hasPolicyStarted = selectedProgram.policy.scheduledStartDate <= new Date()
  const hasActivePolicy =
    hasPolicyStarted &&
    (selectedProgram.policy.scheduledEndDate === undefined ||
      selectedProgram.policy.scheduledEndDate >= new Date())

  const isUpdatePolicyMode = editMode === EditMode.Update
  const isEndPolicyMode = editMode === EditMode.End

  const rolloverEnabled = selectedProgram.policy.rollover !== RolloverOptions.Disabled

  const formHandler = useMemo(() => {
    if (isUpdatePolicyMode) return handleUpdatePolicy
    if (isEndPolicyMode) return handleEndPolicy

    return () => {
      return undefined
    }
  }, [isUpdatePolicyMode, isEndPolicyMode, handleEndPolicy, handleUpdatePolicy])

  return (
    <>
      {!editMode && (
        <ButtonContainer gap={spacingMD}>
          <StyledButton
            id="updateBenefitProgramPolicy.button.label"
            primary
            onClick={() => setEditMode(EditMode.Update)}
          />
          <StyledButton
            id="endBenefitProgramPolicy.button.label"
            primary
            disabled={hasPolicyStarted && !hasActivePolicy}
            onClick={() => setEditMode(EditMode.End)}
          />
        </ButtonContainer>
      )}

      {isUpdatePolicyMode && <UpdateBenefitProgramInfo />}
      {isEndPolicyMode && !hasPolicyStarted && <EndBenefitProgramPolicyInfo />}

      <form onSubmit={handleSubmit(formHandler, handleError)}>
        <ModalBody>
          {!isEndPolicyMode && (
            <FlexRow gap={spacingLG}>
              <FormAmountInput
                fieldName="allowance"
                currencySymbol={currencySymbol}
                errorPresent={!!errors?.allowance?.message}
                labelId="createBenefitProgram.allowance.label"
                disabled={!isUpdatePolicyMode}
                register={register}
                registerOptions={benefitProgramRegisterOptions.allowance}
              />
            </FlexRow>
          )}

          <FlexRow gap={spacingLG}>
            {isUpdatePolicyMode ? (
              <Controller
                control={control}
                name="scheduledStartDate"
                rules={benefitProgramRegisterOptions.updatePolicyStartDate}
                render={({ field: { onChange } }) => (
                  <BenefitProgramStartDateSelector
                    policyDuration={selectedProgram.policy.policyPeriodDuration}
                    annualResetMonth={selectedProgram.policy.annualResetMonth}
                    onChange={onChange}
                  />
                )}
              />
            ) : (
              <>
                <Controller
                  control={control}
                  name="scheduledStartDate"
                  rules={benefitProgramRegisterOptions.scheduledStartDate}
                  render={({ field: { onChange, value } }) => (
                    <FormDatePicker
                      labelId="createBenefitProgram.scheduledStartDate.label"
                      date={value}
                      onChange={onChange}
                      disabled={!!selectedProgram || isEndPolicyMode}
                      hasError={!!errors.scheduledStartDate}
                      style={{ width: isEndPolicyMode ? '50%' : '100%' }}
                    />
                  )}
                />
                {isEndPolicyMode ? (
                  hasPolicyStarted ? (
                    <Controller
                      control={control}
                      name="scheduledEndDate"
                      rules={benefitProgramRegisterOptions.endPolicyEndDate}
                      render={({ field: { onChange } }) => (
                        <BenefitProgramEndDateSelector
                          policyDuration={selectedProgram.policy.policyPeriodDuration}
                          annualResetMonth={selectedProgram.policy.annualResetMonth}
                          onChange={onChange}
                        />
                      )}
                    />
                  ) : null
                ) : (
                  <Controller
                    control={control}
                    name="scheduledEndDate"
                    render={({ field: { onChange, value } }) => (
                      <FormDatePicker
                        labelId="createBenefitProgram.scheduledEndDate.label"
                        date={value}
                        onChange={onChange}
                        disabled={!!selectedProgram}
                        hasError={!!errors.scheduledEndDate}
                        style={{ width: '100%' }}
                      />
                    )}
                  />
                )}
              </>
            )}
          </FlexRow>

          {isEndPolicyMode ? (
            <FlexRow>
              <ControlledSwitch
                label="Delete Future Policies"
                control={control}
                name="deleteFuturePolicies"
              />
            </FlexRow>
          ) : (
            <>
              {!isOneTime && (
                <FlexRow gap={spacingLG}>
                  <ControlledSelect
                    label="Rollover"
                    options={rolloverOptions}
                    disabled={rolloverEnabled || !isUpdatePolicyMode || isAnnual}
                    defaultValue={rollover}
                    setControlValue={setRollover}
                  />
                  <ControlledSwitch
                    label="Ongoing Purchases"
                    control={control}
                    name="ongoingPurchases"
                    disabled={!isUpdatePolicyMode || isAnnual}
                  />
                </FlexRow>
              )}

              {!isOneTime && ongoingPurchasesEnabled && (
                <FlexRow gap={spacingLG}>
                  <FormTextInput
                    fieldName="ongoingPurchaseThreshold"
                    labelId="createBenefitProgram.ongoingPurchaseThreshold.label"
                    register={register}
                    disabled={!isUpdatePolicyMode}
                    errorPresent={!!errors?.ongoingPurchaseThreshold?.message}
                    registerOptions={benefitProgramRegisterOptions.ongoingPurchaseThreshold}
                    normalizeValue={(event) => normalizeTypedAmount(event.target.value)}
                  />
                </FlexRow>
              )}
            </>
          )}

          {editMode && (
            <ButtonContainer>
              <StyledButton primary id="save.label" isLoading={false} />
            </ButtonContainer>
          )}
        </ModalBody>
      </form>
    </>
  )
}
